import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ShieldCheck, Loader2 } from "lucide-react";
import { CredBox, portalOrigin as origin } from "@/components/PortalCredBox";

export const PortalForResidentDialog = ({ resident, buildingName, onClose, onDone }) => {
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState(null);
  const [sending, setSending] = useState("");

  useEffect(() => {
    if (resident) { setEmail(resident.email || ""); setTelefono(resident.telefono || ""); setCreds(null); }
  }, [resident]);

  const create = async () => {
    if (!email.trim() && !telefono.trim()) return toast.error("Inserisci almeno un'email o un telefono");
    setBusy(true);
    try {
      const r = await api.post("/condomino-portals/from-resident", { resident_id: resident.id, email, telefono });
      setCreds({ id: r.data.id, username: r.data.username, password: r.data.password, email: r.data.email, telefono: r.data.telefono, nome: resident?.nome || r.data.contact_name, building_name: buildingName });
      toast.success("Portale creato — contatti salvati in anagrafica");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione portale"); }
    finally { setBusy(false); }
  };

  const send = async (channel) => {
    if (!creds?.id) return;
    setSending(channel);
    try {
      const s = await api.post(`/condomino-portals/${creds.id}/send-credentials`, { channel, password: creds.password, origin_url: origin() });
      toast.success(channel === "email" ? `Inviate via email a ${s.data.to}` : `Inviate via SMS a ${s.data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); }
    finally { setSending(""); }
  };

  return (
    <Dialog open={!!resident} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="portal-resident-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#007BFF]" />Portale di {resident?.nome}</DialogTitle>
          <DialogDescription>Crea o rigenera l'accesso personale. I contatti modificati qui vengono salvati anche nell'anagrafica.</DialogDescription>
        </DialogHeader>
        {creds ? (
          <div className="space-y-4">
            <CredBox creds={creds} onSend={send} sending={sending} />
            <div className="flex justify-end"><Button variant="outline" onClick={onClose} data-testid="close-portal-dialog">Chiudi</Button></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Email</label>
                <Input data-testid="portal-email-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="email@esempio.it" className="h-9 mt-1" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Cellulare (per SMS)</label>
                <Input data-testid="portal-tel-input" value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="+39 ..." className="h-9 mt-1" />
              </div>
            </div>
            {resident?.has_portal && <p className="text-[11px] text-amber-600">Questo condòmino ha già un portale: procedendo verrà rigenerata la password.</p>}
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button data-testid="create-portal-resident-btn" onClick={create} disabled={busy} className="bg-[#007BFF] hover:bg-[#0056B3]">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (resident?.has_portal ? "Rigenera accesso" : "Crea portale")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
